import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { ResultKind } from 'services/base/types';
import { BasePath } from 'services/base/page_path';
import { HistoryService } from 'services/base/history';
import { SessionService } from 'services/accounts/session_service';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { Link } from 'ui/base/link';
import { Theme, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';
import { ActiveButton, DefaultButton } from 'ui/public/button';
import { PartitionedPage } from 'ui/add_pangolin/partitioned_page/partitioned_page';
import baseStyles from './add_pangolin_setup.module.css';
import AddPangolinSetupMessages from './add_pangolin_setup_messages';

type AddPangolinSetupStyles = {
  header: string;
  reversedHeader: string;
  settingsIcon: string;
  title: string;
  instructions: string;
  image: string;
  actions: string;
  action: string;
};

const themedStyles: Record<Theme, AddPangolinSetupStyles> = {
  [Theme.DAYLIGHT]: {
    header: baseStyles.header,
    reversedHeader: baseStyles.reversedHeader,
    settingsIcon: baseStyles.daylightSettingsIcon,
    title: baseStyles.daylightTitle,
    instructions: baseStyles.daylightInstructions,
    image: baseStyles.daylightImage,
    actions: baseStyles.actions,
    action: baseStyles.action,
  },
  [Theme.MIDNIGHT]: {
    header: baseStyles.header,
    reversedHeader: baseStyles.reversedHeader,
    settingsIcon: baseStyles.midnightSettingsIcon,
    title: baseStyles.midnightTitle,
    instructions: baseStyles.midnightInstructions,
    image: baseStyles.midnightImage,
    actions: baseStyles.actions,
    action: baseStyles.action,
  },
};

export const AddPangolinSetup = observer(() => {
  const styles = useStyles(themedStyles);
  const accounts = useAccountsDomain();
  const history = useHistory();
  const handleLogout = React.useCallback(action(async() => {
    const result = await SessionService.logout(accounts);
    if (result.kind === ResultKind.OK || result.kind === ResultKind.USER_ERROR) {
      HistoryService.clear(history);
      HistoryService.replace(history, { kind: BasePath.INTRO });
    }
  }), [accounts, history]);

  let header: React.ReactNode;
  if (accounts.pangolins.size === 0) {
    // Put a link to edit settings if the user has no pangolins yet
    header = (
      <div className={styles.reversedHeader}>
        <Link path={{ kind: BasePath.SETTINGS_EDIT }}>
          <div className={styles.settingsIcon}/>
        </Link>
      </div>
    );
  } else {
    header = (
      <div className={styles.header}>
        <BackButton/>
      </div>
    );
  }

  const top = (
    <>
      {header}
      <div className={styles.title}>
        {AddPangolinSetupMessages.title()}
      </div>
      <div className={styles.instructions}>
        {AddPangolinSetupMessages.instructions()}
      </div>
    </>
  );

  const middle = <div className={styles.image}/>;

  const bottom = (
    <div className={styles.actions}>
      <Link className={styles.action} path={{ kind: BasePath.ADD_PANGOLIN_POWER }}>
        <ActiveButton>
          {AddPangolinSetupMessages.next()}
        </ActiveButton>
      </Link>
      {accounts.pangolins.size <= 0 &&
        <DefaultButton className={styles.action} onClick={handleLogout}>
          {AddPangolinSetupMessages.logout()}
        </DefaultButton>
      }
    </div>
  );

  return (
    <PartitionedPage top={top} middle={middle} bottom={bottom}/>
  );
});
